import type { MaskedGameState, Move } from '@completto/shared';
import { log } from '../lib/logger.ts';
import { pushToast, setSendingMove } from '../stores/index.ts';
import type { MultiplayerService } from './multiplayer.ts';
import type { GameOrchestrator } from './orchestrator.ts';

/** 서버 응답이 5초 안에 도착하지 않으면 사용자에게 알림. */
const SEND_MOVE_TIMEOUT_MS = 5000;

/**
 * MultiplayerService를 GameOrchestrator 인터페이스로 노출.
 * GamePage는 BotMatchService와 동일하게 다룸.
 *
 * sendingMove UX:
 *   sendMove 호출 → uiStore.sendingMove=true
 *   서버가 GameState 또는 GameError 송신 시 → false (해제)
 *   5초 타임아웃 → "전송 실패" 토스트 + false
 */
export class MultiplayerOrchestrator implements GameOrchestrator {
  readonly myPlayerId: string;

  private pendingClientMoveId: string | null = null;
  private pendingTimer: number | null = null;
  private unsubscribeState: (() => void) | null = null;
  private unsubscribeError: (() => void) | null = null;

  constructor(
    private readonly service: MultiplayerService,
    myPlayerId: string,
  ) {
    this.myPlayerId = myPlayerId;
    // pending move가 있을 때만 의미 있음. GameState/Error 한 번 도착하면 클리어.
    this.unsubscribeState = this.service.onGameState(() => this.clearPending());
    this.unsubscribeError = this.service.onGameError((err) => {
      if (err.code === 'STALE_SESSION') {
        pushToast('재연결 중. 잠시 후 다시 시도하세요', 'warn');
      }
      this.clearPending();
    });
  }

  async start(): Promise<void> {
    return Promise.resolve();
  }

  async sendMove(move: Move): Promise<void> {
    const clientMoveId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `m-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    this.pendingClientMoveId = clientMoveId;
    setSendingMove(true);
    if (this.pendingTimer !== null) window.clearTimeout(this.pendingTimer);
    this.pendingTimer = window.setTimeout(
      () => this.handleTimeout(clientMoveId),
      SEND_MOVE_TIMEOUT_MS,
    );
    this.service.sendMove(move, clientMoveId);
  }

  subscribe(fn: (state: MaskedGameState) => void): () => void {
    return this.service.onGameState(fn);
  }

  getState(): MaskedGameState | null {
    return this.service.getLastGameState();
  }

  forceFinish(): void {
    this.service.debugForceFinish();
  }

  /** 페이지 전환 시 정리. */
  dispose(): void {
    if (this.pendingTimer !== null) {
      window.clearTimeout(this.pendingTimer);
      this.pendingTimer = null;
    }
    if (this.unsubscribeState) {
      this.unsubscribeState();
      this.unsubscribeState = null;
    }
    if (this.unsubscribeError) {
      this.unsubscribeError();
      this.unsubscribeError = null;
    }
    this.clearPending();
  }

  private clearPending(): void {
    if (this.pendingClientMoveId === null) return;
    this.pendingClientMoveId = null;
    if (this.pendingTimer !== null) {
      window.clearTimeout(this.pendingTimer);
      this.pendingTimer = null;
    }
    setSendingMove(false);
  }

  private handleTimeout(clientMoveId: string): void {
    if (this.pendingClientMoveId !== clientMoveId) return;
    log.warn('multiplayer.send-timeout', { clientMoveId });
    pushToast('서버 응답 지연. 잠시 후 다시 시도하세요', 'warn');
    this.clearPending();
  }
}
